import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Target, Search, Heart, SlidersHorizontal, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSidebar } from '../../contexts/SidebarContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useAdmin } from '../../contexts/AdminContext';
import PageHeader from '../ui/PageHeader';
import DarkTooltip from '../ui/DarkTooltip';
import PremiumFeatureBlock from '../ui/PremiumFeatureBlock';
import NicheFinderTabs, { TAB_DEFS } from './NicheFinderTabs';
import ChannelCard from './ChannelCard';
import ChannelCardSkeleton from './ChannelCardSkeleton';
import NicheFinderUpsell from './NicheFinderUpsell';
import { useNicheFinderTeaser } from './hooks/useNicheFinderTeaser';

// Abas do teaser: SOMENTE os 7 tiers. NUNCA 'byCategory' nem 'all'
// (decisão de produto: essas duas ficam exclusivas do acesso completo).
const TEASER_TIERS = [
  'exploding', 'rising_fast', 'high_traction',
  'strong_potential', 'recent_traction',
  'mature_monetized', 'established_monetized',
  'outlier', 'new_channels',
];

/**
 * NicheFinderTeaser — vitrine de conversão do Niche Finder para usuários
 * SEM acesso (free/basic/lite). Mostra 1 fileira real (3 cards, amostra
 * fixa do dia) por aba de tier + seção de upsell. Ações bloqueadas
 * (favoritar, busca, filtros) rolam até o upsell e pulsam o CTA.
 */
const NicheFinderTeaser = () => {
  const { t } = useTranslation();
  const { sidebarWidth, isMobile } = useSidebar();
  const { isPrivate } = usePrivacy();
  const { subscriptionData } = useAdmin();

  const { teaser, loading, error } = useNicheFinderTeaser();

  const [activeTabId, setActiveTabId] = useState('exploding');
  const [visitedIds, setVisitedIds] = useState(() => new Set());
  const [pulseKey, setPulseKey] = useState(0);
  const upsellRef = useRef(null);

  // Abas visíveis = tiers com amostra hoje (tier vazio some da fileira de abas).
  const visibleTabs = teaser
    ? TEASER_TIERS.filter((id) => (teaser.samples?.[id] || []).length > 0)
    : TEASER_TIERS;

  // Se a aba ativa sumir (tier sem canais hoje), realoca pra primeira visível.
  useEffect(() => {
    if (teaser && visibleTabs.length > 0 && !visibleTabs.includes(activeTabId)) {
      setActiveTabId(visibleTabs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teaser, activeTabId]);

  const scrollToUpsell = useCallback(() => {
    upsellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Pulso dispara quando o scroll está assentando (não compete com o movimento).
    setTimeout(() => setPulseKey((k) => k + 1), 350);
  }, []);

  const markVisited = useCallback((channelId) => {
    setVisitedIds((prev) => {
      const next = new Set(prev);
      next.add(channelId);
      return next;
    });
  }, []);

  // Erro no teaser → degrada para o bloqueio padrão (comportamento antigo).
  if (error) {
    return <PremiumFeatureBlock />;
  }

  const activeTab = TAB_DEFS.find((tb) => tb.id === activeTabId) || TAB_DEFS[0];
  const channels = teaser?.samples?.[activeTabId] || [];

  // Base sem nenhum canal nos 7 tiers (ex.: base pequena/antiga em dev):
  // sem amostra não há abas, fileira nem borrão — só header + upsell em
  // fluxo NORMAL (sem margem negativa, senão o card "flutua" sobre o vazio).
  const hasSamples = loading || !teaser || visibleTabs.length > 0;

  const lockedBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 34,
    padding: '0 12px',
    fontSize: 13,
    borderRadius: 12,
    fontFamily: 'var(--dp-font)',
    background: 'transparent',
    border: '1px solid var(--dp-border-medium)',
    color: 'var(--dp-text-secondary)',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'border-color 0.15s, color 0.15s',
  };

  return (
    <main
      className="dp-main-content dp-fade-in-up"
      style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'margin-left 0.3s ease',
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        fontFamily: 'var(--dp-font)',
      }}
    >
      {/* Header padrão + pill "Prévia" */}
      <PageHeader
        icon={<Target size={20} style={{ color: 'var(--dp-blue-500)' }} />}
        title={t('viralNiches.title')}
        description={t('viralNiches.subtitle')}
        titleBadge={t('viralNiches.teaser_preview_badge')}
      />

      {/* Nota da prévia (o sorteio diário segue no backend; só não anunciamos) */}
      {hasSamples && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          margin: '-6px 0 12px 2px',
          fontSize: 12,
          color: 'var(--dp-text-tertiary)',
        }}>
          <Sparkles size={13} style={{ flexShrink: 0, color: 'var(--dp-blue-500, #0DAEFF)' }} />
          {t('viralNiches.teaser_sample_note')}
        </div>
      )}

      {/* Abas dos 7 tiers com counts REAIS */}
      {hasSamples && (
        <NicheFinderTabs
          activeTab={activeTabId}
          counts={teaser?.stats}
          onTabChange={(tab) => setActiveTabId(tab.id)}
          visibleTabs={visibleTabs}
        />
      )}

      {/* Linha: descrição da aba + ações BLOQUEADAS (tudo leva ao upsell) */}
      {hasSamples && (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 4,
        marginBottom: 12,
        paddingLeft: 14,
        paddingRight: 12,
        minHeight: 36,
      }}>
        <div style={{
          fontSize: 11,
          color: 'var(--dp-text-tertiary)',
          flex: 1,
          minWidth: 0,
        }}>
          {t(activeTab.descKey)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {!isMobile && (
            <DarkTooltip content={t('viralNiches.teaser_locked_tooltip')}>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{
                  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--dp-text-tertiary)', pointerEvents: 'none',
                }} />
                <input
                  readOnly
                  placeholder={t('viralNiches.search_placeholder')}
                  onFocus={(e) => { e.target.blur(); scrollToUpsell(); }}
                  onClick={scrollToUpsell}
                  style={{
                    width: 170,
                    height: 34,
                    padding: '0 10px 0 30px',
                    borderRadius: 12,
                    border: '1px solid var(--dp-border-medium)',
                    background: 'var(--dp-bg-input)',
                    color: 'var(--dp-text-secondary)',
                    fontSize: 12.5,
                    fontFamily: 'var(--dp-font)',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                />
              </div>
            </DarkTooltip>
          )}
          <DarkTooltip content={t('viralNiches.teaser_locked_tooltip')}>
            <button type="button" style={lockedBtnStyle} onClick={scrollToUpsell}>
              <Heart size={14} />
              <span>{t('viralNiches.favorites_button')}</span>
            </button>
          </DarkTooltip>
          <DarkTooltip content={t('viralNiches.teaser_locked_tooltip')}>
            <button
              type="button"
              aria-label={t('viralNiches.filters_button')}
              style={{ ...lockedBtnStyle, width: 34, padding: 0, justifyContent: 'center' }}
              onClick={scrollToUpsell}
            >
              <SlidersHorizontal size={15} />
            </button>
          </DarkTooltip>
        </div>
      </div>
      )}

      {/* Fileira REAL — 3 cards da amostra do dia (clicáveis) */}
      {hasSamples && (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 12,
      }}>
        {loading || !teaser
          ? [0, 1, 2].map((i) => <ChannelCardSkeleton key={i} />)
          : channels.map((ch) => (
              <ChannelCard
                key={ch.channel_id}
                channel={ch}
                isSuperAdmin={false}
                isPrivacyMode={isPrivate}
                isVisited={visitedIds.has(ch.channel_id)}
                onVisit={markVisited}
                isFavorited={false}
                onToggleFavorite={scrollToUpsell}
              />
            ))}
      </div>
      )}

      {/* Fileira FALSA borrada — SÓ placeholders (nunca dados reais), com fade
          que emenda no upsell. Sugere profundidade do catálogo. */}
      {hasSamples && (
      <div style={{ position: 'relative', marginTop: 12 }} aria-hidden="true">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 12,
          filter: 'blur(7px)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          {[0, 1, 2].map((i) => <ChannelCardSkeleton key={i} />)}
        </div>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, transparent 0%, var(--dp-bg-primary) 78%)',
          pointerEvents: 'none',
        }} />
      </div>
      )}

      {/* Upsell — sobrepõe o fim do borrão (ou fluxo normal se não há amostra) */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        marginTop: hasSamples ? '-104px' : '12px',
        paddingBottom: 32,
      }}>
        <NicheFinderUpsell
          ref={upsellRef}
          stats={teaser?.stats}
          planType={subscriptionData?.plan_type}
          pulseKey={pulseKey}
        />
      </div>
    </main>
  );
};

export default NicheFinderTeaser;
